import * as React from "react";
import { FixedCommentSetEntityType, FixedCommentSetType, SessionType, DeviceType, SubjectType, OverreadStatus, CoughSessionStatus, CoughWaveformType, TimeZone, Language, UnitType, Parameter, CoughEventType } from "webportalclient";
import { Gender, SmokingStatus } from "../misc/api/apiTypes";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { DcfCorrectionType, Country, BloodGlucoseSessionStatus } from "webportalclient";

interface ReferenceContextProviderState {
    genders: Gender[];
    smokingStatuses: SmokingStatus[];
    sessionTypes: SessionType[];
    fixedCommentSetEntityTypes: FixedCommentSetEntityType[];
    fixedCommentSetTypes: FixedCommentSetType[];
    deviceTypes: DeviceType[];
    subjectTypes: SubjectType[];
    overreadStatuses: OverreadStatus[];
    coughSessionStatuses: CoughSessionStatus[];
    coughWaveformTypes: CoughWaveformType[];
    timeZones: TimeZone[];
    countries: Country[];
    languages: Language[];
    unitTypes: UnitType[];
    parameters: Parameter[];
    coughEventTypes: CoughEventType[];
    dcfTypes: DcfCorrectionType[];
    bloodGlucoseSessionStatuses: BloodGlucoseSessionStatus[];
    version: string;
    logoutRedirectUri: string;
}

export const ReferenceContext = React.createContext<ReferenceContextProviderState | undefined>(undefined);

interface ReferenceContextProps {
    version: string;
    logoutRedirectUri: string;
    children: React.ReactNode;
}

export const ReferenceContextProvider = ({ version, logoutRedirectUri, children }: ReferenceContextProps) => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [loading, setLoading] = React.useState(true);
    const [genders, setGenders] = React.useState<Gender[]>([]);
    const [smokingStatuses, setSmokingStatuses] = React.useState<SmokingStatus[]>([]);
    const [sessionTypes, setSessionTypes] = React.useState<SessionType[]>([]);
    const [entityTypes, setEntityTypes] = React.useState<FixedCommentSetEntityType[]>([]);
    const [setTypes, setSetTypes] = React.useState<FixedCommentSetType[]>([]);
    const [deviceTypes, setDeviceTypes] = React.useState<DeviceType[]>([]);
    const [subjectTypes, setSubjectTypes] = React.useState<SubjectType[]>([]);
    const [overreadStatuses, setOverreadStatuses] = React.useState<OverreadStatus[]>([]);
    const [coughSessionStatuses, setCoughSessionStatuses] = React.useState<CoughSessionStatus[]>([]);
    const [coughWaveformTypes, setCoughWaveformTypes] = React.useState<CoughWaveformType[]>([]);
    const [timeZones, setTimeZones] = React.useState<TimeZone[]>([]);
    const [countries, setCountries] = React.useState<Country[]>([]);
    const [languages, setLanguages] = React.useState<Language[]>([]);
    const [unitTypes, setUnitTypes] = React.useState<UnitType[]>([]);
    const [parameters, setParameters] = React.useState<Parameter[]>([]);
    const [coughEventTypes, setCoughEventTypes] = React.useState<CoughEventType[]>([]);
    const [dcfTypes, setDcfTypes] = React.useState<DcfCorrectionType[]>([]);
    const [bloodGlucoseSessionStatuses, setBloodGlucoseSessionStatuses] = React.useState<BloodGlucoseSessionStatus[]>([]);

    React.useEffect(() => {
        const getData = async () => {
            setLoading(true);

            const gendersResult = await legacyClient.getGenders();
            setGenders(gendersResult);

            const smokingStatusesResult = await legacyClient.getSmokingStatuses();
            setSmokingStatuses(smokingStatusesResult);

            const sessionTypesResult = await client.client.getSessionTypes();
            setSessionTypes(sessionTypesResult.value);

            const entityTypesResult = await client.client.getFixedCommentSetEntityTypes();
            setEntityTypes(entityTypesResult.value);

            const setTypesResult = await client.client.getFixedCommentSetTypes();
            setSetTypes(setTypesResult.value);

            const deviceTypesResult = await client.client.getDeviceTypes();
            setDeviceTypes(deviceTypesResult.value);

            const subjectTypesResult = await client.client.getSubjectTypes();
            setSubjectTypes(subjectTypesResult.value);

            const overreadStatusesResult = await client.client.getOverreadStatuses();
            setOverreadStatuses(overreadStatusesResult.value);

            const coughSessionStatusesResult = await client.client.getCoughSessionStatuses();
            setCoughSessionStatuses(coughSessionStatusesResult.value);

            const coughWaveformTypeResult = await client.client.getCoughWaveformTypes();
            setCoughWaveformTypes(coughWaveformTypeResult.value);

            const timeZonesResult = await client.client.getTimeZones(undefined, undefined, "sortOrder desc");
            setTimeZones(timeZonesResult.value);

            const countriesResult = await client.client.getCountries();
            setCountries(countriesResult.value);

            const languageResult = await client.client.getLanguages(undefined, undefined, "name asc");
            setLanguages(languageResult.value);

            const unitTypeResult = await client.client.getUnitTypes();
            setUnitTypes(unitTypeResult.value);

            const parametersResult = await client.client.getParameters();
            setParameters(parametersResult.value);

            const coughEventTypesResult = await client.client.getCoughEventTypes();
            setCoughEventTypes(coughEventTypesResult.value);

            const dcfTypesResult = await client.client.getDcfCorrectionTypes();
            setDcfTypes(dcfTypesResult.value);

            const bloodGlucoseSessionStatuses = await client.client.getBloodGlucoseSessionStatuses();
            setBloodGlucoseSessionStatuses(bloodGlucoseSessionStatuses.value);

            setLoading(false);
        }

        getData();
    }, []);

    if (loading)
        return <LoadingWithMessage message="Loading..." />

    return (
        <ReferenceContext.Provider
            value={{
                genders: genders,
                smokingStatuses: smokingStatuses,
                sessionTypes: sessionTypes,
                fixedCommentSetEntityTypes: entityTypes,
                fixedCommentSetTypes: setTypes,
                deviceTypes: deviceTypes,
                subjectTypes: subjectTypes,
                overreadStatuses: overreadStatuses,
                coughSessionStatuses: coughSessionStatuses,
                coughWaveformTypes: coughWaveformTypes,
                timeZones: timeZones,
                countries: countries,
                languages: languages,
                unitTypes: unitTypes,
                parameters: parameters,
                coughEventTypes: coughEventTypes,
                dcfTypes: dcfTypes,
                bloodGlucoseSessionStatuses: bloodGlucoseSessionStatuses,
                version: version,
                logoutRedirectUri: logoutRedirectUri
            }}
        >
            {children}
        </ReferenceContext.Provider>
    );
}

export const useReferenceContextProvider = () => {
    const context = React.useContext(ReferenceContext);
    if (context === undefined) {
        throw new Error('useReferenceContextProvider must be used within a ReferenceContext Provider')
    }

    return context;
}
